import React, { useState, useEffect } from 'react';
import useToken from '../../useToken';
import { baseUrl } from '../../config';
import { StarsRow } from './RatingListModal';
import './RatingComponent.css';

const LABELS = {
    1: 'Mala',
    2: 'Regular',
    3: 'Buena',
    4: 'Muy buena',
    5: 'Excelente',
};

const StarsInput = ({ value, onChange, size = 28 }) => {
    const [hover, setHover] = useState(0);
    const shown = hover || value;

    return (
        <div
            className="stars-input"
            role="radiogroup"
            aria-label="Calificación"
            onMouseLeave={() => setHover(0)}
        >
            {[1, 2, 3, 4, 5].map((num) => (
                <button
                    key={num}
                    type="button"
                    role="radio"
                    aria-checked={value === num}
                    aria-label={`${num} de 5${LABELS[num] ? `, ${LABELS[num]}` : ''}`}
                    className={'star-btn' + (num <= shown ? ' is-on' : '')}
                    onMouseEnter={() => setHover(num)}
                    onFocus={() => setHover(num)}
                    onBlur={() => setHover(0)}
                    onClick={() => onChange(num)}
                >
                    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 3.2l2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 16.4 6.8 19.2l1-5.8L3.6 9.3l5.8-.8L12 3.2z" />
                    </svg>
                </button>
            ))}
        </div>
    );
};

const RatingComponent = ({ idBooking, onRated, disabled = false, existingRatingData = null }) => {
    const { token } = useToken();
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [existingRating, setExistingRating] = useState(null);

    useEffect(() => {
        if (existingRatingData) {
            setExistingRating(existingRatingData);
            setRating(existingRatingData.rating);
            setComment(existingRatingData.comment || '');
        } else if (token && idBooking) {
            checkExistingRating();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, idBooking, existingRatingData]);

    const checkExistingRating = async () => {
        try {
            const response = await fetch(`${baseUrl}Booking/rating/${idBooking}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json();
            if (data.success && data.data) {
                setExistingRating(data.data);
                setRating(data.data.rating);
                setComment(data.data.comment || '');
                setSubmitted(true);
            }
        } catch (error) {
            console.error('Error checking existing rating:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!rating) return;

        setLoading(true);
        try {
            const response = await fetch(`${baseUrl}Booking/rate/${idBooking}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ rating, comment: rating < 5 ? comment : '' }),
            });

            const data = await response.json();
            if (data.success) {
                setSubmitted(true);
                setExistingRating({
                    rating,
                    comment: rating < 5 ? comment : '',
                    createdAt: new Date().toISOString(),
                });
                if (onRated) onRated(rating);
            } else {
                alert(data.message || 'Error al guardar la calificación');
            }
        } catch (error) {
            console.error('Error submitting rating:', error);
            alert('Error al enviar la calificación');
        } finally {
            setLoading(false);
        }
    };

    if (existingRating && submitted) {
        const fecha = existingRating.createdAt
            ? new Date(existingRating.createdAt).toLocaleDateString('es-MX', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            })
            : '';

        return (
            <div className="rating-component is-submitted">
                <div className="rating-component__result">
                    <StarsRow value={existingRating.rating} size={22} />
                    <strong>{Number(existingRating.rating).toFixed(0)}/5</strong>
                    {LABELS[existingRating.rating] && (
                        <span className="rating-component__label">{LABELS[existingRating.rating]}</span>
                    )}
                </div>
                {existingRating.comment ? (
                    <p className="comment-display">{existingRating.comment}</p>
                ) : null}
                {fecha ? <small>Calificado el {fecha}</small> : null}
            </div>
        );
    }

    if (disabled) return null;

    return (
        <div className="rating-component">
            <form onSubmit={handleSubmit}>
                <StarsInput value={rating} onChange={setRating} />
                <p className="rating-component__hint">
                    {rating ? LABELS[rating] : 'Selecciona de 1 a 5 estrellas'}
                </p>

                {rating > 0 && rating < 5 && (
                    <div className="comment-section">
                        <label htmlFor={`rating-comment-${idBooking}`}>Comentario (opcional)</label>
                        <textarea
                            id={`rating-comment-${idBooking}`}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="¿Qué podríamos mejorar?"
                            maxLength={500}
                            rows={3}
                        />
                    </div>
                )}

                <button
                    type="submit"
                    disabled={!rating || loading}
                    className="submit-rating-btn"
                >
                    {loading ? 'Enviando…' : 'Enviar calificación'}
                </button>
            </form>
        </div>
    );
};

export default RatingComponent;
